import React, {
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react';
import {
    get as _get,
    isNil as _isNil
} from 'lodash';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from 'e1p-portals-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { WizardSingleErrorComponent } from 'gw-portals-wizard-components-ui';
import metadata from '../PaymentDetailsPage.metadata.json5';
import '../PaymentDetailsPage.messages';

const LOB = 'personalAuto_EA';
const INITIAL_PAYMENT_OPTION_PATH = 'baseData.value.conversionDTO.initialPaymentType';

function ViewPaymentDetailsPage(props) {
    const {
        wizardData: submissionVM
    } = props;
    const [producerCodeOptions, setProducerCodeOptions] = useState([]);
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const PAY_MONTHLY = 'af:payMthly3';

    const availableValuesForPaymentOptions = useMemo(() => {
        const correctOffering = submissionVM.lobData.personalAuto_EA.offerings.value[0];

        if (correctOffering.paymentPlans !== undefined) {
            return correctOffering.paymentPlans
                .filter((paymentPlans) => paymentPlans.name)
                .reduce((paymentPlanMap, paymentPlan) => ({
                    ...paymentPlanMap,
                    [paymentPlan.billingId]: paymentPlan
                }), {});
        }

        return {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(submissionVM.lobData.personalAuto_EA.offerings.value)]);
    
    const getTotalPremium = () => {
        const selectedPlan = availableValuesForPaymentOptions[
            _get(submissionVM, 'bindData.selectedPaymentPlan.value')
        ]?.total;

        if (selectedPlan) {
            const totalPremium = selectedPlan.amount;

            return { currency: 'usd', amount: totalPremium };
        }

        return undefined;
    };


    /**
     * Define property overrides for this Jutro component.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const overrideProps = {
        '@field': {
            showOptional: false,
            labelPosition: 'top',
            readOnly: true
        },
        paymentDetailsPageLoadingIndicator: {
            loaded: true,
        },
        paymentDetailsPageContainer: {
            visible: true
        },
        iframePaymentus: {
            visible: false
        },
        iFrameLoader: {
            loaded: true,
        },
        totalPremiumID: {
            value: getTotalPremium()
        },
        totalPremiumWithFullPayID: {
            value: !_isNil(availableValuesForPaymentOptions[PAY_MONTHLY]) ? availableValuesForPaymentOptions[PAY_MONTHLY].total : undefined
        },
        chooseLicenceProDiv: {
            // eslint-disable-next-line no-prototype-builtins
            visible: submissionVM.value.hasOwnProperty('licensedProducers')
        },
        mailingAndBillingAddressComponent: {
            transactionVM: submissionVM,
            updateWizardData: () => {},
            onValidate: () => { },
            viewOnlyMode: true,
            lob: LOB
        },
        producerCode: {
            readOnly: true
        },
        agencyName: {
            readOnly: true
        },
        paymentOptionsID: {
            submissionVM,
            authHeader,
            updateWizardData: () => {},
            LOB,
            viewOnly: true
        },
        quoteProposalLinkContainer: {
            disabled: true
        },
        iframePDF: {
            visible: false
        },
        signButton: {
            visible: false
        },
        signPolicyLoader: {
            loaded: true
        },
        tpiComponentId: {
            submissionVM,
            viewModelService,
            authHeader,
            updateWizardData: () => {},
            viewOnlyMode: true
        },
        signatureType: {
            value: _get(submissionVM, 'baseData.value.signatureType_ext', '')
        },
        producerCodeDrpDwn: {
            // eslint-disable-next-line no-prototype-builtins
            visible: submissionVM.value.hasOwnProperty('licensedProducers'),
            value: _get(submissionVM, 'bindData.value.licensedProducerID_Ext', '')
        },
        policyTermComponent: {
            transactionVM: submissionVM,
            authHeader,
            updateWizardData: () => {},
            viewOnly: true // as discussed with satish donot allow user to change on payment page
        },
        paymentusComponent: {
            visible: false
        },
        monthlyPaymentScheduleComponent: {
            visible: false
        },
        signatureComponent: {
            submissionVM,
            updateWizardData: () => {},
            authHeader,
            LOB,
            onSignatureSuccess: () => { },
            producerCodeOptions,
            setProducerCodeOptions,
            viewOnlyMode: true,
            visible: true
        },
        insuredContactInfo: {
            viewOnlyMode: true,
            showPaperless: true
        },
        paymentOptions: {
            value: _get(submissionVM, INITIAL_PAYMENT_OPTION_PATH),
            visible: !!_get(submissionVM, INITIAL_PAYMENT_OPTION_PATH) 
        },
        // IAP-371 show TPI component in only read only Mode
        e1ptpiDisplayTableContainer: {
            transactionVM: submissionVM,
            viewModelService,
            authHeader,
            updateWizardData: () => {},
            setIsSavingTPI: () => { },
            showErrors: false,
            onValidate: () => { },
            disregardFieldValidationParentPage: () => { },
            viewOnlyMode: true,
            showTitle: false
        },
        addTPIButton: {
            visible: false
        },
        quoteProposalFailureErrorDiv: {
            visible: false
        },
        paperlessEmailChangedMessageDiv:{
            visible: false
        },
        propertyPolicyRequiredErrorDiv: {
            visible: false
        },
        userNotAuthorisedErrorDiv: {
            visible: false
        }
    };


    const resolvers = {
        resolveCallbackMap: {
            signPolicy: undefined
        },
        resolveComponentMap: {
            WizardSingleErrorComponent
        }
    };

    const readValue = useCallback(
        (id, path) => readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps),
        [submissionVM, overrideProps]
    );

    return (
        <WizardPage
            showNext={false}
            shouldLink
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                resolveValue={readValue}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

ViewPaymentDetailsPage.propTypes = wizardProps;
export default ViewPaymentDetailsPage;
